// Изотермы: одна линия равной температуры поверх слоя SST. Геометрию считает
// браузер (marchingSquares.ts) по сетке, скачанной на вьюпорт; здесь — только
// стиль слоя и вспомогательные функции.
//
// Линия рисуется ДВУМЯ слоями: белая подложка (casing) под тёмной линией. Поле
// SST красится coolwarm — от синего к красному; одноцветная линия терялась бы
// то на одном конце, то на другом. Белый ореол читается над обоими.

import { mapColorOf } from '../brand/mapColors'
import { OVERLAY_PREFIX } from './layerOrder'

export const ISOTHERM_PREFIX = OVERLAY_PREFIX + 'isotherm-'

/** Шаг ползунка градуса, °C. Капитан задаёт изотерму «по полградуса». */
export const ISOTHERM_STEP = 0.5

/** Цвет линии — гаммы домена (`brand/mapColors.ts`). Функция, а не константа:
 *  бренд приезжает ответом `/cabinet/site`, то есть позже загрузки модуля. */
export function isothermLineColor(): string {
  return mapColorOf('isotherm')
}

/** Цвет ПОВЕРХНОСТНОЙ изотермы. Отдельный ключ гаммы, а не оттенок глубинной:
 *  две линии на карте отвечают на разные вопросы (что на горизонте Контекста и
 *  что на 0 м), и различать их обязан цвет, а не память смотрящего. */
export function isothermSurfaceColor(): string {
  return mapColorOf('isotherm-surface')
}

/** Белая подложка общая на все бренды: это не цвет, а приём читаемости. Поле
 *  SST красится от синего к красному, и одноцветная линия терялась бы то на
 *  одном конце, то на другом — ореол читается над обоими. */
export const ISOTHERM_CASING_COLOR = '#ffffff'

const LINE_WIDTH = 1.4
const CASING_EXTRA = 2.2 // насколько подложка шире линии (ореол с каждой стороны)
export const ISOTHERM_CASING_OPACITY = 0.65

export type IsothermPart = 'line' | 'casing' | 'labels'

export function isothermLayerId(key: string, part: IsothermPart): string {
  return `${ISOTHERM_PREFIX}${key}-${part}`
}

export function isothermSourceId(key: string): string {
  return `${ISOTHERM_PREFIX}src-${key}`
}

/** Источник ТОЧЕК подписи — отдельный от линий.
 *
 *  Подписи точечные, а не вдоль линии (`symbol-placement: line-center`), потому
 *  что marching squares отдаёт геометрию НЕСШИТЫМИ двухточечными отрезками:
 *  line-center поставил бы ярлык в каждую пересечённую ячейку сетки. Изобаты
 *  могут позволить себе line-center ровно потому, что их полилинии приходят
 *  сшитыми с сервера. */
export function isothermLabelSourceId(key: string): string {
  return `${ISOTHERM_PREFIX}lbl-src-${key}`
}

/** Префикс id картинки-подписи — под data-driven `icon-image`
 *  (`['concat', ISO_LABEL_PREFIX, ['to-string', ['get','temp']]]`). */
export const ISO_LABEL_PREFIX = 'iso-lbl-'

export function isothermLabelImageId(temp: number): string {
  return `${ISO_LABEL_PREFIX}${temp}`
}

/** Ниже этого зума ярлыков нет. Порог ниже изобатного (7): изотермы читают и на
 *  обзорных кадрах. Но и не ноль — линий в семействе до шести десятков, и на
 *  самом мелком зуме плашки съели бы карту. */
export const ISO_LABEL_MIN_ZOOM = 4

/** Округлить к сетке ISOTHERM_STEP: ползунок и клавиатура не должны давать
 *  «12.3456°», иначе подпись изотермы выглядит как шум датчика. */
export function snapToStep(value: number): number {
  return Math.round(value / ISOTHERM_STEP) * ISOTHERM_STEP
}

/** Толщина линии по зуму: на обзорных тоньше (изотермы вьются и сливаются),
 *  на рабочих — в номинал, на предельных чуть заметнее. */
export function isothermWidthExpr(width: number) {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    3,
    width * 0.75,
    7,
    width,
    12,
    width * 1.5,
  ]
}

export const isothermLineWidthExpr = () => isothermWidthExpr(LINE_WIDTH)
export const isothermCasingWidthExpr = () => isothermWidthExpr(LINE_WIDTH + CASING_EXTRA)
